import React, { useState, useEffect, useCallback } from 'react';
import { dealerAPI } from '../../services/dealerAPI';

const fmt = (val) =>
  val !== undefined && val !== null
    ? `₹${Number(val).toLocaleString('en-IN')}`
    : '—';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    if (document.getElementById('razorpay-script')) {
      const interval = setInterval(() => {
        if (window.Razorpay) { clearInterval(interval); resolve(true); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const DealerPaymentModal = ({ offer, carLabel, onClose, onSuccess }) => {
  const [step,          setStep]          = useState('phone');
  const [phone,         setPhone]         = useState('');
  const [phoneError,    setPhoneError]    = useState('');
  const [processing,    setProcessing]    = useState(false);
  const [errMsg,        setErrMsg]        = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    if (!offer?.id) return;
    dealerAPI
      .getPaymentStatus(offer.id)
      .then((res) => {
        const s = res.data?.status;
        if (s === 'success')   setPaymentStatus('success');
        if (s === 'initiated') setPaymentStatus('initiated');
      })
      .catch(() => {});
  }, [offer?.id]);

  const validatePhone = () => {
    if (!phone.trim()) {
      setPhoneError('Phone number is required');
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(phone.trim())) {
      setPhoneError('Enter a valid 10-digit Indian mobile number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handlePay = useCallback(async () => {
    if (!validatePhone()) return;

    setProcessing(true);
    setStep('processing');
    setErrMsg('');

    try {
      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) throw new Error('Failed to load Razorpay SDK. Check your internet connection.');

      const initRes = await dealerAPI.initiatePayment(offer.id, {
        phone:         phone.trim(),
        paymentMethod: 'razorpay',
      });

      const { razorpay_order_id, amount_paisa, razorpay_key } = initRes.data;

      const razorpayOptions = {
        key:         razorpay_key,
        amount:      amount_paisa,
        currency:    'INR',
        name:        'Caryugo',
        description: `Payment for ${carLabel}`,
        order_id:    razorpay_order_id,

        // ── Prefill contact + test UPI VPA so UPI tab appears ──────────
        prefill: {
          contact: `91${phone.trim()}`,
          vpa:     'success@razorpay', // test UPI — remove in production
        },

        // ── Explicitly enable all methods including UPI ─────────────────
        method: {
          upi:         true,
          card:        true,
          netbanking:  true,
          wallet:      true,
          paylater:    true,
          emi:         false,
        },

        theme: { color: '#003566' },

        modal: {
          ondismiss: () => {
            setProcessing(false);
            setStep('phone');
          },
        },

        handler: async (response) => {
          try {
            await dealerAPI.confirmPayment(offer.id, {
              razorpayOrderId:   response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setStep('success');
            onSuccess?.();
          } catch (err) {
            setErrMsg(
              err.response?.data?.error || 'Payment confirmation failed. Contact support.'
            );
            setStep('error');
          }
        },
      };

      const rzp = new window.Razorpay(razorpayOptions);
      rzp.on('payment.failed', (res) => {
        setErrMsg(res.error?.description || 'Payment failed.');
        setStep('error');
      });
      rzp.open();

    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Something went wrong. Please try again.';
      setErrMsg(msg);
      setStep('error');
    } finally {
      setProcessing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id, phone, carLabel]);

  const amount = Number(offer?.offer_price || 0);

  if (paymentStatus === 'success') {
    return (
      <Overlay onClose={onClose}>
        <SuccessView carLabel={carLabel} amount={amount} onClose={onClose} alreadyPaid />
      </Overlay>
    );
  }

  return (
    <Overlay onClose={step !== 'processing' ? onClose : undefined}>
      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes popIn  { from { opacity:0; transform:scale(0.85); } to { opacity:1; transform:scale(1); } }
      `}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg,#003566,#001d3d)',
        padding: '20px 24px',
        borderBottom: '3px solid #FFD60A',
        borderRadius: '16px 16px 0 0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#FFD60A', fontSize: 18, fontWeight: 800 }}>
            💳 Complete Payment
          </h2>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
            {carLabel}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,214,10,0.7)', fontWeight: 700, textTransform: 'uppercase' }}>
            Amount Due
          </p>
          <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#FFD60A' }}>
            {fmt(amount)}
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 24, overflowY: 'auto', maxHeight: 'calc(90vh - 160px)' }}>

        {/* STEP: phone */}
        {step === 'phone' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>

            {/* Payment methods strip */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 12, marginBottom: 24, padding: '14px 16px',
              background: '#f8f9fa', borderRadius: 12, border: '1px solid #e9ecef',
            }}>
              {[
                { icon: '📱', label: 'UPI' },
                { icon: '💳', label: 'Card' },
                { icon: '🏦', label: 'Net Banking' },
                { icon: '👛', label: 'Wallet' },
                { icon: '🕐', label: 'Pay Later' },
              ].map(({ icon, label }) => (
                <div key={label} style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 20, lineHeight: 1 }}>{icon}</div>
                  <div style={{ fontSize: 9, color: '#6b7280', fontWeight: 600, marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>

            <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#003566', fontSize: 15 }}>
              Your Mobile Number
            </p>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666' }}>
              Required for UPI / payment confirmation and receipt.
            </p>

            <div style={{ position: 'relative', marginBottom: 6 }}>
              <span style={{
                position: 'absolute', left: 14, top: '50%',
                transform: 'translateY(-50%)',
                fontWeight: 700, color: '#003566', fontSize: 14,
              }}>+91</span>
              <input
                type="tel"
                maxLength={10}
                placeholder="98XXXXXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                style={{
                  width: '100%', padding: '13px 16px 13px 52px',
                  border: `2px solid ${phoneError ? '#dc3545' : '#e0e0e0'}`,
                  borderRadius: 10, fontSize: 16, fontWeight: 600,
                  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#003566')}
                onBlur={(e)  => (e.target.style.borderColor = phoneError ? '#dc3545' : '#e0e0e0')}
                onKeyDown={(e) => e.key === 'Enter' && handlePay()}
              />
            </div>
            {phoneError && (
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#dc3545' }}>{phoneError}</p>
            )}

            {paymentStatus === 'initiated' && (
              <div style={{
                background: '#fef3c7', border: '1px solid #fbbf24',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: '#92400e',
              }}>
                ⏳ You have an incomplete payment. Enter your number to resume.
              </div>
            )}

            <button
              onClick={handlePay}
              disabled={processing}
              style={{ ...btnStyle(processing), marginTop: 12 }}
            >
              {processing ? '⏳ Processing…' : '🔒 Proceed to Pay'}
            </button>

            <p style={{ margin: '12px 0 0', fontSize: 11, color: '#999', textAlign: 'center' }}>
              🔐 Powered by Razorpay · UPI · Cards · Net Banking · Wallets
            </p>
          </div>
        )}

        {/* STEP: processing */}
        {step === 'processing' && (
          <div style={{ textAlign: 'center', padding: '40px 20px', animation: 'fadeIn 0.3s ease' }}>
            <div style={{
              width: 56, height: 56,
              border: '5px solid #e0e0e0', borderTopColor: '#003566',
              borderRadius: '50%', animation: 'spin 0.9s linear infinite',
              margin: '0 auto 20px',
            }} />
            <p style={{ fontWeight: 700, color: '#003566', fontSize: 16, margin: '0 0 8px' }}>
              Opening Payment Gateway…
            </p>
            <p style={{ color: '#666', fontSize: 13, margin: 0 }}>
              Please complete the payment in the Razorpay window.
            </p>
          </div>
        )}

        {/* STEP: success */}
        {step === 'success' && (
          <SuccessView carLabel={carLabel} amount={amount} onClose={onClose} />
        )}

        {/* STEP: error */}
        {step === 'error' && (
          <div style={{ textAlign: 'center', padding: '32px 20px', animation: 'popIn 0.3s ease' }}>
            <p style={{ fontSize: 48, margin: '0 0 16px' }}>❌</p>
            <p style={{ fontWeight: 800, color: '#dc3545', fontSize: 18, margin: '0 0 8px' }}>Payment Failed</p>
            <p style={{ color: '#666', fontSize: 14, margin: '0 0 24px' }}>{errMsg}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => { setStep('phone'); setErrMsg(''); }}
                style={{ padding: '10px 24px', background: '#003566', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
              >Try Again</button>
              <button
                onClick={onClose}
                style={{ padding: '10px 24px', background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
              >Close</button>
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
};

const Overlay = ({ children, onClose }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}
    onClick={onClose}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: '#fff', borderRadius: 16,
        width: '100%', maxWidth: 460, maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
        animation: 'popIn 0.25s ease',
      }}
    >
      {children}
    </div>
  </div>
);

const SuccessView = ({ carLabel, amount, onClose, alreadyPaid = false }) => (
  <div style={{ textAlign: 'center', padding: '40px 24px' }}>
    <div style={{
      width: 72, height: 72,
      background: 'linear-gradient(135deg,#28a745,#20c997)',
      borderRadius: '50%', margin: '0 auto 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 32, boxShadow: '0 0 0 12px #28a74520',
      animation: 'popIn 0.4s ease',
    }}>✓</div>
    <h3 style={{ margin: '0 0 8px', color: '#003566', fontSize: 22, fontWeight: 800 }}>
      {alreadyPaid ? 'Payment Already Done!' : 'Payment Successful! 🎉'}
    </h3>
    <p style={{ color: '#666', fontSize: 14, margin: '0 0 6px' }}>{carLabel}</p>
    <p style={{ color: '#28a745', fontWeight: 800, fontSize: 20, margin: '0 0 24px' }}>{fmt(amount)}</p>
    {!alreadyPaid && (
      <p style={{ color: '#666', fontSize: 13, margin: '0 0 24px', lineHeight: 1.5 }}>
        Your payment has been received. Admin will process the deal finalization shortly.
      </p>
    )}
    <button onClick={onClose} style={btnStyle(false)}>Done ✓</button>
  </div>
);

const btnStyle = (disabled) => ({
  width: '100%', padding: '14px',
  background: disabled ? '#ccc' : 'linear-gradient(135deg,#003566,#001d3d)',
  border: disabled ? 'none' : '2px solid #FFD60A',
  borderRadius: 10,
  color: disabled ? '#888' : '#FFD60A',
  fontWeight: 800, fontSize: 15,
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s', letterSpacing: 0.3,
});

export default DealerPaymentModal;